import { useEffect, useState } from 'react'
import {
  fetchMyIODevices,
  fetchOrganizationPositions,
  forgetIODevice,
  setDeviceRoute,
} from '../../api/ws'
import { useAppStore } from '../../store/app'
import type { IODevice } from '../../store/types'
import { PairForm } from './PairForm'

// A routable target for the picker: the position id plus a friendly label that
// includes the agent's name when one is live for that position, e.g.
// "Thomas (Footman)".
type RouteOption = { id: string; label: string }

// groupVoiceDevices splits a flat list of shared voice devices into bridges
// (top-level paired devices) and the endpoints nested under each (voice "C"). An
// endpoint links to its bridge via bridge_io_device_id; a bridge has none. Endpoints
// are sorted by name for stable display. Exported for unit testing.
export function groupVoiceDevices(voiceDevices: IODevice[]): {
  bridges: IODevice[]
  endpointsByBridge: Map<string, IODevice[]>
} {
  const bridges = voiceDevices.filter((d) => !d.bridge_io_device_id)
  const endpointsByBridge = new Map<string, IODevice[]>()
  for (const d of voiceDevices) {
    if (!d.bridge_io_device_id) continue
    const arr = endpointsByBridge.get(d.bridge_io_device_id) ?? []
    arr.push(d)
    endpointsByBridge.set(d.bridge_io_device_id, arr)
  }
  for (const arr of endpointsByBridge.values()) {
    arr.sort((a, b) => a.name.localeCompare(b.name))
  }
  return { bridges, endpointsByBridge }
}

// Onboarding + management for VOICE devices.
//
// A Home Assistant BRIDGE (and its satellites) is per-agent (Model 1): which agent a
// wake word reaches is chosen on the HA side (one conversation entity per agent you
// publish via the agent's "Reachable by voice" toggle), NOT by routing the device
// here — so bridges show no "routes to" picker; they're listed for reference.
//
// A PERSONAL puck is a non-pipeline voice device: it has no per-agent entities, so it
// still routes to an agent by its own device route (Model 2), and keeps the picker.
// The same applies to any future non-HA shared voice device.
export function VoiceDevicesSheet() {
  const orgID = useAppStore((s) => s.organizationID)
  const devices = useAppStore((s) => s.ioDevices)
  const positions = useAppStore((s) => s.organizationPositions)
  const agentInstances = useAppStore((s) => s.agentInstances)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    fetchMyIODevices()
    if (orgID) fetchOrganizationPositions(orgID)
  }, [orgID])

  const voiceDevices = devices.filter(
    (d) =>
      d.multi_human_user &&
      d.form_factor === 'voice' &&
      (!orgID || d.multi_user_organization_id === orgID),
  )
  // Personal voice pucks (docs/personal-devices-plan.md): bound to the current user,
  // not the household, so they carry no owning-org and never appear above. They're in
  // this list only because they're the viewer's own (personal devices are single-owner
  // and only their owner ever sees them), so routing them here is inherently owner-only
  // — and wsSetDeviceRoute enforces owner-only on the server regardless.
  const personalPucks = devices.filter(
    (d) => !d.multi_human_user && d.form_factor === 'voice',
  )
  // A voice route should point at a durable position — temp/eliminated ones
  // are the wrong target (they retire out from under the route).
  const routablePositions = positions.filter(
    (p) => !p.eliminated_at && !p.is_temp,
  )

  // Best agent name per position (prefer an active instance), so the picker can
  // read "Thomas (Footman)" rather than just "Footman".
  const agentByPosition = new Map<string, string>()
  for (const inst of agentInstances) {
    if (inst.retired_at || !inst.organization_position_id || !inst.name) continue
    const existing = agentByPosition.get(inst.organization_position_id)
    if (!existing || inst.status === 'active') {
      agentByPosition.set(inst.organization_position_id, inst.name)
    }
  }
  const routeOptions: RouteOption[] = routablePositions.map((p) => {
    const agent = agentByPosition.get(p.id)
    return { id: p.id, label: agent ? `${agent} (${p.name})` : p.name }
  })

  const { bridges, endpointsByBridge } = groupVoiceDevices(voiceDevices)

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      {error && <Banner tone="error">{error}</Banner>}
      {notice && <Banner tone="ok">{notice}</Banner>}

      <PairForm
        orgID={orgID}
        deviceKind="voice"
        onError={(e) => {
          setNotice(null)
          setError(e)
        }}
        onPaired={(name) => {
          setError(null)
          setNotice(`Connected “${name}”. Publish agents for voice to reach them.`)
          fetchMyIODevices()
        }}
      />

      <h3
        style={{
          margin: '20px 0 8px',
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          opacity: 0.6,
        }}
      >
        Voice
      </h3>
      {bridges.length === 0 ? (
        <p style={{ opacity: 0.6 }}>
          No Home Assistant bridge connected in this household yet.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {bridges.map((b) => {
            const endpoints = endpointsByBridge.get(b.id) ?? []
            const common = {
              onError: (e: string) => {
                setNotice(null)
                setError(e)
              },
              onClearError: () => setError(null),
              onMutated: fetchMyIODevices,
            }
            // A bridge that reported satellites renders as a group; a bare paired
            // bridge (not yet reported) as a single row. Neither shows a route picker
            // — HA bridges are per-agent (chosen on the HA side).
            return endpoints.length > 0 ? (
              <BridgeGroup key={b.id} bridge={b} endpoints={endpoints} {...common} />
            ) : (
              <VoiceDeviceRow key={b.id} device={b} showRoute={false} {...common} />
            )
          })}
        </ul>
      )}

      {personalPucks.length > 0 && (
        <>
          <h3
            style={{
              margin: '20px 0 8px',
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              opacity: 0.6,
            }}
          >
            Personal speakers
          </h3>
          <p style={{ margin: '0 0 8px', fontSize: 12, opacity: 0.6 }}>
            Yours alone — only you can route these, and what they hear is treated as
            coming from you.
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {personalPucks.map((d) => (
              <VoiceDeviceRow
                key={d.id}
                device={d}
                showRoute
                options={routeOptions}
                onError={(e) => {
                  setNotice(null)
                  setError(e)
                }}
                onClearError={() => setError(null)}
                onMutated={fetchMyIODevices}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function Banner({ tone, children }: { tone: 'error' | 'ok'; children: React.ReactNode }) {
  const palette =
    tone === 'error'
      ? { background: '#fee', color: '#900' }
      : { background: '#e6ffed', color: '#046b2f' }
  return (
    <div
      style={{
        padding: '8px 12px',
        borderRadius: 4,
        marginBottom: 12,
        ...palette,
      }}
    >
      {children}
    </div>
  )
}

// RoutePicker is the "Routes to" dropdown that writes a device's route via
// set_device_route. Used only for devices that route per-device (Model 2) — today
// personal pucks; a future non-HA shared voice device would use it too. HA bridges
// are per-agent and never show it.
function RoutePicker({
  ioDeviceID,
  value,
  options,
  onError,
  onClearError,
  onMutated,
}: {
  ioDeviceID: string
  value: string
  options: RouteOption[]
  onError: (e: string) => void
  onClearError: () => void
  onMutated: () => void
}) {
  const [busy, setBusy] = useState(false)

  async function changeRoute(positionID: string) {
    onClearError()
    setBusy(true)
    try {
      const resp = await setDeviceRoute({ ioDeviceID, positionID })
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError((e as Error)?.message ?? String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, opacity: 0.7 }}>Routes to</span>
      <select
        value={value}
        disabled={busy}
        onChange={(e) => changeRoute(e.target.value)}
        style={{ fontSize: 16 }}
      >
        <option value="">Not routed — pick an agent</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

// forgetDevice confirms and un-pairs a bridge/device (server soft-removes; a
// bridge's endpoints cascade with it). Returns whether it succeeded so the caller
// can refresh.
async function forgetDevice(
  device: IODevice,
  extraWarning: string,
  onError: (e: string) => void,
  onClearError: () => void,
): Promise<boolean> {
  if (
    !confirm(
      `Remove “${device.name}”?\n\nThe device will stop working until it's ` +
        'paired again.' +
        extraWarning,
    )
  ) {
    return false
  }
  onClearError()
  try {
    const resp = await forgetIODevice(device.id)
    if (resp?.error) {
      onError(resp.error)
      return false
    }
    return true
  } catch (e) {
    onError((e as Error)?.message ?? String(e))
    return false
  }
}

// A single paired voice device. For a shared/HA device (showRoute=false) it's just a
// name + remove — the agent is chosen per-wake-word on the HA side. A personal puck
// (showRoute=true) keeps the "Routes to" picker (Model 2).
function VoiceDeviceRow({
  device,
  showRoute,
  options = [],
  onError,
  onClearError,
  onMutated,
}: {
  device: IODevice
  showRoute: boolean
  options?: RouteOption[]
  onError: (e: string) => void
  onClearError: () => void
  onMutated: () => void
}) {
  const [busy, setBusy] = useState(false)

  async function remove() {
    setBusy(true)
    if (await forgetDevice(device, '', onError, onClearError)) onMutated()
    setBusy(false)
  }

  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontWeight: 600 }}>{device.name}</div>
      {showRoute && (
        <RoutePicker
          ioDeviceID={device.id}
          value={device.route_position_id ?? ''}
          options={options}
          onError={onError}
          onClearError={onClearError}
          onMutated={onMutated}
        />
      )}
      <div>
        <button onClick={remove} disabled={busy} style={{ color: '#900' }}>
          Remove
        </button>
      </div>
    </li>
  )
}

// A bridge (one HA install) with its satellites nested beneath it, shown for
// reference. No route pickers — an HA bridge is per-agent (pick the agent by wake
// word in Home Assistant; publish agents for voice in their settings). Satellites
// aren't individually removable (they come from the HA inventory report); only the
// bridge carries Remove (its satellites cascade with it).
function BridgeGroup({
  bridge,
  endpoints,
  onError,
  onClearError,
  onMutated,
}: {
  bridge: IODevice
  endpoints: IODevice[]
  onError: (e: string) => void
  onClearError: () => void
  onMutated: () => void
}) {
  const [busy, setBusy] = useState(false)
  const count = endpoints.length
  const satWord = count === 1 ? 'satellite' : 'satellites'

  async function remove() {
    setBusy(true)
    const ok = await forgetDevice(
      bridge,
      `\n\nThis also removes its ${count} ${satWord}.`,
      onError,
      onClearError,
    )
    if (ok) onMutated()
    setBusy(false)
  }

  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div
        style={{
          background: '#f5f6f8',
          border: '1px solid #e6e8ec',
          borderRadius: 8,
          padding: '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600 }}>{bridge.name}</span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              padding: '1px 6px',
              borderRadius: 4,
              background: '#e0e4ea',
              color: '#4a5568',
            }}
          >
            Bridge
          </span>
        </div>
        <div style={{ fontSize: 12, opacity: 0.65 }}>
          One Home Assistant hub fronting {count} voice {satWord}. Pick which agent a
          wake word reaches in Home Assistant; publish agents for voice in their
          settings.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            opacity: 0.55,
          }}
        >
          {count} {satWord}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            paddingLeft: 12,
            marginLeft: 2,
            borderLeft: '2px solid #eee',
          }}
        >
          {endpoints.map((ep) => (
            <SatelliteRow key={ep.id} endpoint={ep} />
          ))}
        </div>
      </div>

      <div>
        <button onClick={remove} disabled={busy} style={{ color: '#900' }}>
          Remove bridge
        </button>
      </div>
    </li>
  )
}

// One satellite (per-room voice device) under a bridge: its name and HA entity id,
// for reference. No routing — agent selection is per wake word in Home Assistant.
function SatelliteRow({ endpoint }: { endpoint: IODevice }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontWeight: 600, fontSize: 13 }}>{endpoint.name}</div>
      {endpoint.external_ref && (
        <div
          style={{
            fontSize: 11,
            opacity: 0.5,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}
        >
          {endpoint.external_ref}
        </div>
      )}
    </div>
  )
}
